import { useEffect, useState } from 'react';
import { createRoute, stripSearchParams } from '@tanstack/react-router';

import { rootRoute, useAppRouterInitial } from '../routerBase';
import {
  ORGANISATIONS_SEARCH_DEFAULTS,
  parseOrganisationsSearch,
} from '../../hooks/useOrganisationRouterSearch';
import { TableViewSkeleton } from '../../components/Skeleton/TableViewSkeleton';
import { OrganisationEditSkeleton } from '../../components/Skeleton/OrganisationEditSkeleton';
import { DetailsSkeleton } from '../../components/Skeleton/DetailsSkeleton';
import { useOrganisation } from '../../hooks/organisations';
import { useTranslation } from '../../hooks/useTranslation';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
// Type-only imports so the per-route bundles stay split — the runtime modules
// are loaded via dynamic import() inside each route's effect (see below).
import type OrganisationManagement from '../../components/OrganisationManagement/index';
import type EditOrganisation from '../../components/EditOrganisation/index';
import type DetailsOrganisation from '../../components/DetailsOrganisation/index';

export const organisationsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/organisations',
  validateSearch: parseOrganisationsSearch,
  search: { middlewares: [stripSearchParams(ORGANISATIONS_SEARCH_DEFAULTS)] },
  component: function OrganisationsIndexRoute() {
    const { initialOrganisations, currentUser } = useAppRouterInitial();
    const { t } = useTranslation();
    // First render renders the skeleton so React hydration byte-matches the
    // Phlex skeleton; the real component is dynamic-imported in an effect so
    // it stays in its own chunk.
    useDocumentTitle(t('common.page_titles.organisations'));
    const [Component, setComponent] = useState<typeof OrganisationManagement | null>(null);
    useEffect(() => {
      import('../../components/OrganisationManagement/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component) {
      return <TableViewSkeleton label={t('common.loading_organisations')} />;
    }
    return <Component initialData={initialOrganisations} currentUser={currentUser} />;
  },
});

export const organisationEditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/organisations/$id/edit',
  component: function OrganisationEditRoute() {
    const { initialOrganisation } = useAppRouterInitial();
    const { id } = organisationEditRoute.useParams();
    const idNum = Number(id);
    // Seed from the SSR detail when present; otherwise fetch so a client-side
    // navigation (without the full-document reload) still loads the record.
    const seed =
      initialOrganisation && initialOrganisation.id === idNum
        ? { organisation: initialOrganisation }
        : undefined;
    const { data } = useOrganisation(idNum, { initialData: seed });
    const { t } = useTranslation();
    useDocumentTitle(
      data ? t('common.page_titles.edit', { name: data.name }) : t('common.page_titles.organisations'),
    );
    const [Component, setComponent] = useState<typeof EditOrganisation | null>(null);
    useEffect(() => {
      import('../../components/EditOrganisation/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component || !data) {
      return <OrganisationEditSkeleton />;
    }
    return <Component organisationId={idNum} initialOrganisation={data} />;
  },
});

export const organisationDetailsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/organisations/$id',
  component: function OrganisationDetailsRoute() {
    const { initialOrganisation, currentUser } = useAppRouterInitial();
    const { id } = organisationDetailsRoute.useParams();
    const { t } = useTranslation();
    const idNum = Number(id);
    // Seed from the SSR detail when present; otherwise fetch so a client-side
    // navigation (without the full-document reload) still loads the record.
    const seed =
      initialOrganisation && initialOrganisation.id === idNum
        ? { organisation: initialOrganisation }
        : undefined;
    const { data } = useOrganisation(idNum, { initialData: seed });
    useDocumentTitle(data?.name ?? t('common.page_titles.organisations'));
    const [Component, setComponent] = useState<typeof DetailsOrganisation | null>(null);
    useEffect(() => {
      import('../../components/DetailsOrganisation/index').then((mod) => {
        setComponent(() => mod.default);
      });
    }, []);
    if (!Component || !data) {
      return <DetailsSkeleton label={t('common.loading')} />;
    }
    return <Component organisationId={idNum} initialOrganisation={data} currentUser={currentUser} />;
  },
});
